import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Send, CheckCircle, ArrowLeft, Paperclip, X } from 'lucide-react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    phone: '',
    message: ''
  });
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const MAX_FILES = 5;
  const MAX_SIZE = 10 * 1024 * 1024; // 10 MB
  // Any image (screenshots from Android/iPhone/Windows/macOS: PNG, JPG, WEBP, HEIC…) + PDF
  const isAllowed = (f) => (f.type || '').startsWith('image/') || f.type === 'application/pdf';

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFiles = (e) => {
    setError('');
    const picked = Array.from(e.target.files || []);
    const valid = [];
    for (const f of picked) {
      if (!isAllowed(f)) { setError('Dozwolone tylko obrazy i pliki PDF.'); continue; }
      if (f.size > MAX_SIZE) { setError(`Plik "${f.name}" jest za duży (max 10 MB).`); continue; }
      valid.push(f);
    }
    setFiles(prev => [...prev, ...valid].slice(0, MAX_FILES));
    e.target.value = ''; // allow re-picking the same file
  };

  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const fd = new FormData();
      fd.append('name', formData.name);
      fd.append('email', formData.email);
      fd.append('subject', formData.subject);
      fd.append('phone', formData.phone);
      fd.append('message', formData.message);
      files.forEach(f => fd.append('files', f));
      // Note: don't set Content-Type manually — axios/browser add the multipart boundary.
      await axios.post(`${API}/contact/submit`, fd);
      setSuccess(true);
      setFormData({ name: '', email: '', subject: '', phone: '', message: '' });
      setFiles([]);
    } catch (err) {
      setError('Wystąpił błąd. Spróbuj ponownie.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#050505] min-h-screen">
      <Navbar hideMenu={true} />

      <main className="section-padding pt-32">
        <div className="container-custom max-w-2xl">
          {/* Back link */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-[#9CA3AF] hover:text-[#10b981] transition-colors mb-8"
            data-testid="back-to-home"
          >
            <ArrowLeft size={18} />
            <span>Wróć na stronę główną</span>
          </Link>

          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-white mb-4" data-testid="contact-title">Kontakt</h1>
            <p className="text-[#9CA3AF]">
              Masz pytania dotyczące systemu lub zamówienia? Napisz do nas — odpowiadamy najszybciej, jak to możliwe.
            </p>
          </div>

          {success ? (
            <div className="glass-card p-8 text-center" data-testid="success-message">
              <div className="w-16 h-16 bg-[#10b981]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-[#10b981]" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Wiadomość wysłana!</h2>
              <p className="text-[#9CA3AF] mb-6">Odpowiemy tak szybko, jak to możliwe.</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button onClick={() => setSuccess(false)} className="btn-ghost">Wyślij kolejną wiadomość</button>
                <Link to="/" className="btn-primary">← Wróć na stronę główną</Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="glass-card p-8 space-y-6" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-[#9CA3AF] mb-2">Imię i nazwisko</label>
                  <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Jan Kowalski" className="w-full" data-testid="input-name" />
                </div>
                <div>
                  <label className="block text-sm text-[#9CA3AF] mb-2">E-mail *</label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="twoj@email.com" className="w-full" required data-testid="input-email" />
                </div>
              </div>

              <div>
                <label className="block text-sm text-[#9CA3AF] mb-2">Temat</label>
                <input type="text" name="subject" value={formData.subject} onChange={handleChange} placeholder="W czym możemy pomóc?" className="w-full" data-testid="input-subject" />
              </div>

              <div>
                <label className="block text-sm text-[#9CA3AF] mb-2">Numer telefonu (opcjonalnie)</label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="+48 123 456 789" className="w-full" data-testid="input-phone" />
              </div>

              <div>
                <label className="block text-sm text-[#9CA3AF] mb-2">Wiadomość *</label>
                <textarea name="message" value={formData.message} onChange={handleChange} placeholder="Treść wiadomości..." rows={5} className="w-full resize-none" required data-testid="input-message" />
              </div>

              {/* Attachments */}
              <div>
                <label className="block text-sm text-[#9CA3AF] mb-2">Załączniki (opcjonalnie)</label>
                <label className="flex items-center justify-center gap-2 w-full py-3 rounded-lg border border-dashed border-white/15 text-[#9CA3AF] hover:border-[#10b981] hover:text-[#10b981] cursor-pointer transition-colors" data-testid="input-files">
                  <Paperclip size={18} />
                  <span className="text-sm">Dodaj zrzuty ekranu lub pliki (obrazy, PDF — max 5, do 10 MB)</span>
                  <input type="file" multiple accept="image/*,application/pdf" onChange={handleFiles} className="hidden" />
                </label>
                {files.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {files.map((f, i) => (
                      <div key={i} className="flex items-center justify-between gap-3 bg-[#0B0F0E] border border-white/10 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <Paperclip size={14} className="text-[#6B7280] flex-shrink-0" />
                          <span className="text-sm text-[#e5e7eb] truncate">{f.name}</span>
                          <span className="text-xs text-[#6B7280] flex-shrink-0">{Math.round(f.size / 1024)} KB</span>
                        </div>
                        <button type="button" onClick={() => removeFile(i)} className="text-[#6B7280] hover:text-red-400 flex-shrink-0" title="Usuń">
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {error && <p className="text-red-400 text-sm">{error}</p>}

              <button type="submit" disabled={loading} className="btn-primary" data-testid="submit-contact">
                {loading ? <div className="spinner"></div> : <><Send size={18} /> Wyślij</>}
              </button>
            </form>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;